import { useState, useRef, useEffect } from "react";

const fieldOptions = [
  { id: "f1", label: "用气量", category: "用气数据", icon: "ri-bar-chart-line" },
  { id: "f2", label: "营收金额", category: "财务数据", icon: "ri-money-cny-circle-line" },
  { id: "f3", label: "用户数量", category: "用户数据", icon: "ri-user-3-line" },
  { id: "f4", label: "欠费金额", category: "财务数据", icon: "ri-error-warning-line" },
  { id: "f5", label: "安检完成率", category: "安检数据", icon: "ri-shield-check-line" },
  { id: "f6", label: "设备在线率", category: "设备数据", icon: "ri-cpu-line" },
  { id: "f7", label: "告警次数", category: "告警数据", icon: "ri-alarm-warning-line" },
  { id: "f8", label: "购气笔数", category: "营业数据", icon: "ri-shopping-cart-2-line" },
];

const chartTypes = [
  { id: "bar", label: "柱状图", icon: "ri-bar-chart-2-line" },
  { id: "line", label: "折线图", icon: "ri-line-chart-line" },
  { id: "pie", label: "饼图", icon: "ri-pie-chart-line" },
  { id: "table", label: "数据表格", icon: "ri-table-line" },
];

const mockTableData = [
  { month: "2026-01", gasUsed: "312.5万m³", revenue: "¥725,680", users: "127,234", arrears: "¥52,340", safetyRate: "93.2%", deviceOnline: "96.8%" },
  { month: "2026-02", gasUsed: "298.3万m³", revenue: "¥685,420", users: "127,890", arrears: "¥48,920", safetyRate: "94.1%", deviceOnline: "97.1%" },
  { month: "2026-03", gasUsed: "285.6万m³", revenue: "¥810,230", users: "128,456", arrears: "¥45,680", safetyRate: "94.2%", deviceOnline: "97.2%" },
];

export default function ReportBuilder() {
  const [selectedFields, setSelectedFields] = useState<string[]>(["f1", "f2", "f3"]);
  const [chartType, setChartType] = useState("bar");
  const [dateRange, setDateRange] = useState("month");
  const [reportName, setReportName] = useState("自定义营收分析报告");
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<"pdf" | "excel" | "csv">("pdf");
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [summaryText, setSummaryText] = useState("");
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const summaryTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const toggleField = (id: string) => {
    setSelectedFields(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  };

  const drawChart = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const data = [65, 82, 78, 91, 88, 95, 72, 85, 90, 88, 76, 93];
    const labels = ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"];
    const padL = 30, padR = 10, padT = 15, padB = 25;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;

    // Grid
    for (let i = 0; i <= 4; i++) {
      const y = padT + (chartH / 4) * i;
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(W - padR, y);
      ctx.strokeStyle = "rgba(0,0,0,0.06)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = "#9ca3af";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(String(100 - i * 25), padL - 4, y + 3);
    }

    if (chartType === "bar" || chartType === "table") {
      const barW = chartW / data.length;
      data.forEach((v, i) => {
        const barH = (v / 100) * chartH;
        const x = padL + i * barW + barW * 0.15;
        const bw = barW * 0.7;
        const y = padT + chartH - barH;
        const grad = ctx.createLinearGradient(0, y, 0, padT + chartH);
        grad.addColorStop(0, i === 11 ? "#E8531A" : "rgba(232,83,26,0.7)");
        grad.addColorStop(1, "rgba(232,83,26,0.1)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.roundRect(x, y, bw, barH, 2);
        ctx.fill();
        ctx.fillStyle = "#9ca3af";
        ctx.font = "8px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(labels[i], x + bw / 2, H - 6);
      });
    } else if (chartType === "line") {
      const grad = ctx.createLinearGradient(0, padT, 0, padT + chartH);
      grad.addColorStop(0, "rgba(232,83,26,0.2)");
      grad.addColorStop(1, "rgba(232,83,26,0)");
      ctx.beginPath();
      data.forEach((v, i) => {
        const x = padL + (i / (data.length - 1)) * chartW;
        const y = padT + chartH - (v / 100) * chartH;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      const lastX = padL + chartW;
      const lastY = padT + chartH;
      ctx.lineTo(lastX, lastY);
      ctx.lineTo(padL, lastY);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.beginPath();
      data.forEach((v, i) => {
        const x = padL + (i / (data.length - 1)) * chartW;
        const y = padT + chartH - (v / 100) * chartH;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = "#E8531A";
      ctx.lineWidth = 2;
      ctx.lineJoin = "round";
      ctx.stroke();
      data.forEach((v, i) => {
        const x = padL + (i / (data.length - 1)) * chartW;
        const y = padT + chartH - (v / 100) * chartH;
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fillStyle = "#E8531A";
        ctx.fill();
        ctx.fillStyle = "#9ca3af";
        ctx.font = "8px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(labels[i], x, H - 6);
      });
    } else if (chartType === "pie") {
      const cx = W / 2, cy = H / 2, r = Math.min(W, H) / 2 - 20;
      const pieData = [
        { label: "居民用户", value: 68.5, color: "#E8531A" },
        { label: "工商业", value: 23.2, color: "#f97316" },
        { label: "工业", value: 8.3, color: "#fbbf24" },
      ];
      let startAngle = -Math.PI / 2;
      pieData.forEach(d => {
        const angle = (d.value / 100) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, r, startAngle, startAngle + angle);
        ctx.closePath();
        ctx.fillStyle = d.color;
        ctx.fill();
        const midAngle = startAngle + angle / 2;
        const lx = cx + (r * 0.65) * Math.cos(midAngle);
        const ly = cy + (r * 0.65) * Math.sin(midAngle);
        ctx.fillStyle = "white";
        ctx.font = "bold 10px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(`${d.value}%`, lx, ly + 3);
        startAngle += angle;
      });
    }
  };

  useEffect(() => {
    if (generated) drawChart();
  }, [generated, chartType]);

  const handleGenerate = () => {
    setGenerating(true);
    setSummaryText("");
    setGeneratingSummary(false);
    setTimeout(() => {
      setGenerating(false);
      setGenerated(true);
      setTimeout(() => {
        setGeneratingSummary(true);
        const text = `本报表涵盖${selectedFields.length}项数据维度，时间范围${dateRange === "month" ? "本月" : dateRange === "quarter" ? "本季度" : "本年"}。营收同比增长12.3%，用气量增长8.7%。欠费率5.3%较上月降0.8%，安检完成率94.2%。建议重点关注朝阳区欠费用户，及时开展催缴工作，预计可回收¥32,000+。`;
        let i = 0;
        if (summaryTimerRef.current) clearInterval(summaryTimerRef.current);
        summaryTimerRef.current = setInterval(() => {
          if (i < text.length) {
            setSummaryText(text.slice(0, i + 1));
            i += 3;
          } else {
            setSummaryText(text);
            if (summaryTimerRef.current) clearInterval(summaryTimerRef.current);
            setGeneratingSummary(false);
          }
        }, 20);
      }, 300);
    }, 2000);
  };

  const handleExport = () => {
    setExporting(true);
    setTimeout(() => {
      setExporting(false);
      setExportDone(true);
      setExportModal(false);
      setTimeout(() => setExportDone(false), 3000);
    }, 2200);
  };

  const handleSendEmail = () => {
    setEmailSent(true);
    setTimeout(() => {
      setEmailModal(false);
      setEmailSent(false);
    }, 2000);
  };

  useEffect(() => () => { if (summaryTimerRef.current) clearInterval(summaryTimerRef.current); }, []);

  return (
    <div className="space-y-4">
      {exportDone && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">
          <i className="ri-check-double-line text-lg"></i>
          <span>导出成功！{exportFormat.toUpperCase()} 文件已下载到本地</span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-50">
            <i className="ri-tools-line text-[#E8531A] text-sm"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">自定义报表生成器</h3>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">报表名称</label>
            <input value={reportName} onChange={e => setReportName(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">时间范围</label>
            <select value={dateRange} onChange={e => setDateRange(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]">
              <option value="day">今日</option>
              <option value="week">本周</option>
              <option value="month">本月</option>
              <option value="quarter">本季度</option>
              <option value="year">本年</option>
              <option value="custom">自定义</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="text-xs text-gray-500 mb-2 block">选择数据字段（已选 {selectedFields.length} 项）</label>
          <div className="grid grid-cols-4 gap-2">
            {fieldOptions.map(field => (
              <button
                key={field.id}
                onClick={() => toggleField(field.id)}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-colors cursor-pointer ${selectedFields.includes(field.id) ? "border-[#E8531A] bg-orange-50" : "border-gray-100 hover:border-gray-200"}`}
              >
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${selectedFields.includes(field.id) ? "bg-[#E8531A]/10" : "bg-gray-50"}`}>
                  <i className={`${field.icon} text-sm ${selectedFields.includes(field.id) ? "text-[#E8531A]" : "text-gray-400"}`}></i>
                </div>
                <span className={`text-[10px] font-medium ${selectedFields.includes(field.id) ? "text-[#E8531A]" : "text-gray-500"}`}>{field.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <label className="text-xs text-gray-500 mb-2 block">图表类型</label>
          <div className="flex gap-2">
            {chartTypes.map(ct => (
              <button
                key={ct.id}
                onClick={() => setChartType(ct.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${chartType === ct.id ? "border-[#E8531A] bg-orange-50 text-[#E8531A]" : "border-gray-100 text-gray-500 hover:border-gray-200"}`}
              >
                <i className={ct.icon}></i>{ct.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <button onClick={handleGenerate} disabled={generating || selectedFields.length === 0} className="flex items-center gap-2 bg-[#E8531A] text-white px-5 py-2.5 rounded-lg text-sm font-medium cursor-pointer hover:bg-[#d44a15] disabled:opacity-60 whitespace-nowrap">
            {generating ? <><i className="ri-loader-4-line animate-spin"></i>生成中...</> : <><i className="ri-robot-2-line"></i>AI生成报表</>}
          </button>
          {generated && (
            <>
              <button onClick={() => setExportModal(true)} className="flex items-center gap-2 bg-[#E8531A]/10 text-[#E8531A] border border-[#E8531A]/30 px-4 py-2.5 rounded-lg text-sm cursor-pointer hover:bg-[#E8531A]/20 whitespace-nowrap">
                <i className="ri-download-2-line"></i>导出报表
              </button>
              <button onClick={() => setEmailModal(true)} className="flex items-center gap-2 border border-gray-200 text-gray-600 px-4 py-2.5 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                <i className="ri-mail-send-line text-teal-500"></i>邮件发送
              </button>
              <button className="flex items-center gap-2 border border-gray-200 text-gray-600 px-4 py-2.5 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                <i className="ri-printer-line text-gray-500"></i>打印
              </button>
            </>
          )}
        </div>
      </div>

      {/* Generated preview */}
      {generated && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-gray-900">{reportName}</h3>
              <p className="text-xs text-gray-400 mt-0.5">生成时间: 2026-04-10 09:41 · AI自动生成 · {selectedFields.length}项数据维度</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded-full font-medium">已生成</span>
              <button onClick={() => setExportModal(true)} className="flex items-center gap-1.5 bg-[#E8531A] text-white px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
                <i className="ri-download-2-line"></i>导出
              </button>
            </div>
          </div>

          {/* Chart */}
          {chartType !== "table" && (
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <canvas ref={canvasRef} width={800} height={200} className="w-full" />
            </div>
          )}

          {/* Data table */}
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-3 py-2 text-gray-500 font-medium rounded-l-lg">月份</th>
                  {selectedFields.includes("f1") && <th className="text-right px-3 py-2 text-gray-500 font-medium">用气量</th>}
                  {selectedFields.includes("f2") && <th className="text-right px-3 py-2 text-gray-500 font-medium">营收金额</th>}
                  {selectedFields.includes("f3") && <th className="text-right px-3 py-2 text-gray-500 font-medium">用户数量</th>}
                  {selectedFields.includes("f4") && <th className="text-right px-3 py-2 text-gray-500 font-medium">欠费金额</th>}
                  {selectedFields.includes("f5") && <th className="text-right px-3 py-2 text-gray-500 font-medium">安检完成率</th>}
                  {selectedFields.includes("f6") && <th className="text-right px-3 py-2 text-gray-500 font-medium rounded-r-lg">设备在线率</th>}
                </tr>
              </thead>
              <tbody>
                {mockTableData.map((row, i) => (
                  <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/50">
                    <td className="px-3 py-2.5 text-gray-700 font-medium">{row.month}</td>
                    {selectedFields.includes("f1") && <td className="px-3 py-2.5 text-right text-gray-600">{row.gasUsed}</td>}
                    {selectedFields.includes("f2") && <td className="px-3 py-2.5 text-right text-[#E8531A] font-medium">{row.revenue}</td>}
                    {selectedFields.includes("f3") && <td className="px-3 py-2.5 text-right text-gray-600">{row.users}</td>}
                    {selectedFields.includes("f4") && <td className="px-3 py-2.5 text-right text-amber-600">{row.arrears}</td>}
                    {selectedFields.includes("f5") && <td className="px-3 py-2.5 text-right text-green-600">{row.safetyRate}</td>}
                    {selectedFields.includes("f6") && <td className="px-3 py-2.5 text-right text-teal-600">{row.deviceOnline}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* AI Summary */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-[#E8531A]/15 p-4">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-sparkling-2-line text-[#E8531A] text-sm"></i>
              <span className="text-xs font-semibold text-[#E8531A]">AI智能摘要</span>
              {generatingSummary && (
                <span className="flex items-center gap-1 text-xs text-[#E8531A]/60">
                  <i className="ri-loader-4-line animate-spin text-xs"></i>生成中
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              {summaryText}
              {generatingSummary && <span className="inline-block w-0.5 h-3 bg-[#E8531A] animate-pulse ml-0.5 align-middle"></span>}
            </p>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {exportModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !exporting && setExportModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[480px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-900">导出报表</h3>
              {!exporting && (
                <button onClick={() => setExportModal(false)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                  <i className="ri-close-line text-gray-500"></i>
                </button>
              )}
            </div>

            {exporting ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-loader-4-line animate-spin text-[#E8531A] text-3xl"></i>
                </div>
                <div className="text-sm font-medium text-gray-700 mb-1">正在生成 {exportFormat.toUpperCase()} 文件...</div>
                <div className="text-xs text-gray-400 mb-4">AI正在排版报表内容，请稍候</div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div className="bg-[#E8531A] h-2 rounded-full transition-all duration-300 animate-pulse" style={{ width: "65%" }}></div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-5">
                  <label className="text-xs text-gray-500 mb-3 block">选择导出格式</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { key: "pdf", label: "PDF文档", icon: "ri-file-pdf-2-line", color: "text-red-500", desc: "适合打印分享" },
                      { key: "excel", label: "Excel表格", icon: "ri-file-excel-2-line", color: "text-green-600", desc: "适合数据分析" },
                      { key: "csv", label: "CSV数据", icon: "ri-file-text-line", color: "text-gray-600", desc: "适合系统导入" },
                    ].map(fmt => (
                      <button
                        key={fmt.key}
                        onClick={() => setExportFormat(fmt.key as typeof exportFormat)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-colors cursor-pointer ${exportFormat === fmt.key ? "border-[#E8531A] bg-orange-50" : "border-gray-100 hover:border-gray-200"}`}
                      >
                        <i className={`${fmt.icon} text-2xl ${fmt.color}`}></i>
                        <div className="text-center">
                          <div className={`text-xs font-semibold ${exportFormat === fmt.key ? "text-[#E8531A]" : "text-gray-700"}`}>{fmt.label}</div>
                          <div className="text-[10px] text-gray-400 mt-0.5">{fmt.desc}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-5 space-y-2">
                  <label className="text-xs text-gray-500 block">导出内容</label>
                  {[
                    { label: "报表数据（含所有选中字段）", checked: true },
                    { label: "AI智能摘要", checked: true },
                    { label: "图表截图（仅PDF）", checked: exportFormat === "pdf" },
                    { label: "数据统计汇总行", checked: true },
                  ].map((opt, i) => (
                    <label key={i} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" defaultChecked={opt.checked} className="accent-[#E8531A]" />
                      <span className="text-sm text-gray-700">{opt.label}</span>
                    </label>
                  ))}
                </div>

                <div className="mb-5">
                  <label className="text-xs text-gray-500 mb-1.5 block">文件名称</label>
                  <input
                    type="text"
                    defaultValue={`${reportName}_2026-04-10`}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]"
                  />
                </div>

                <div className="flex gap-2">
                  <button onClick={() => setExportModal(false)} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
                  <button onClick={handleExport} className="flex-1 bg-[#E8531A] text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
                    <i className="ri-download-2-line mr-1"></i>确认导出 {exportFormat.toUpperCase()}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Email Modal */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEmailModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[440px]" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 mb-4">定时邮件发送</h3>
            {emailSent ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-double-line text-green-500 text-2xl"></i>
                </div>
                <div className="text-sm font-bold text-gray-900">发送成功！</div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">收件人</label>
                  <input type="email" defaultValue="manager@gascloud.com" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">抄送</label>
                  <input type="email" placeholder="多个邮箱用逗号分隔" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">发送方式</label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="sendMode" defaultChecked className="accent-[#E8531A]" /><span className="text-sm text-gray-700">立即发送</span></label>
                    <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="sendMode" className="accent-[#E8531A]" /><span className="text-sm text-gray-700">定时发送</span></label>
                    <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="sendMode" className="accent-[#E8531A]" /><span className="text-sm text-gray-700">定期发送</span></label>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">附件格式</label>
                  <div className="flex gap-2">
                    {["PDF", "Excel", "Word"].map(fmt => (
                      <label key={fmt} className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" defaultChecked={fmt === "PDF"} className="accent-[#E8531A]" />
                        <span className="text-sm text-gray-700">{fmt}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setEmailModal(false)} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
                  <button onClick={handleSendEmail} className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">确认发送</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
